const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY as string | undefined;
const GEMINI_MODEL =
  (import.meta.env.VITE_GEMINI_MODEL as string | undefined) || "gemini-2.5-flash";
const MAX_RETRIES = 3;
const INITIAL_BACKOFF = 2000;

export type ResponseLanguage = "auto" | "english" | "gujarati";

export interface CompetitiveExamAiRequest {
  message: string;
  responseLanguage: ResponseLanguage;
  topics: string[];
}

function buildSystemPrompt(responseLanguage: ResponseLanguage, topics: string[]) {
  const topicText = topics.length ? topics.join(", ") : "general competitive exam topics";

  return `You are Sanmaya AI, a competitive exam tutor for India-focused exams.
You support Gujarati and English. User messages may be in Gujarati, English, or mixed.
When introducing yourself or signing off when asked, use the name Sanmaya AI.

Scope:
- Indian history
- Indian state history
- Indian constitution and polity
- Gujarati grammar
- English grammar
- General competitive exam preparation and related current static knowledge

Selected topic focus: ${topicText}.

Response rules:
1) Stay inside the exam-focused scope above.
2) If user asks outside scope, politely refuse and ask an exam-related question.
3) Keep explanations concise, correct, and exam-ready.
4) When useful, include 3-5 bullet key points and one short memory trick.
5) If asked for quiz/test, produce MCQs with answer key.
6) If factual confidence is low, clearly mention uncertainty.
7) Output language mode: ${responseLanguage}. If mode is auto, respond in the user's primary language.`;
}

async function callGeminiApi(body: CompetitiveExamAiRequest, retryCount = 0): Promise<string> {
  if (!GEMINI_API_KEY) {
    throw new Error("VITE_GEMINI_API_KEY is missing");
  }

  const response = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent?key=${GEMINI_API_KEY}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        systemInstruction: {
          parts: [{ text: buildSystemPrompt(body.responseLanguage, body.topics) }],
        },
        contents: [
          {
            role: "user",
            parts: [{ text: body.message }],
          },
        ],
        generationConfig: {
          temperature: 0.3,
          maxOutputTokens: 700,
        },
      }),
    }
  );

  if (response.status === 429 && retryCount < MAX_RETRIES) {
    const delay = INITIAL_BACKOFF * Math.pow(2, retryCount);
    await new Promise((resolve) => setTimeout(resolve, delay));
    return callGeminiApi(body, retryCount + 1);
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      `Gemini error ${response.status}: ${errorData?.error?.message || "Unknown error"}`
    );
  }

  const data = await response.json();
  return data?.candidates?.[0]?.content?.parts?.[0]?.text || "";
}

export async function askCompetitiveExamAi(body: CompetitiveExamAiRequest) {
  try {
    return await callGeminiApi(body);
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("AI is not reachable. Check a valid VITE_GEMINI_API_KEY, then restart dev server.");
  }
}
